import React, { useState, useEffect } from "react";
import { Plus, Landmark, Trash2, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { funds as api, type Fund, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CREATE_INIT = { name: "", type: "SINKING" as Fund["type"] };
const TXN_INIT = { type: "CONTRIBUTION" as "CONTRIBUTION" | "WITHDRAWAL", amount: "", transactionDate: "", note: "" };

const FundsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(CREATE_INIT);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [txnTarget, setTxnTarget] = useState<Fund | null>(null);
  const [txnForm, setTxnForm] = useState(TXN_INIT);
  const [txnSaving, setTxnSaving] = useState(false);
  const [txnError, setTxnError] = useState("");

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmFund, setConfirmFund] = useState<Fund | null>(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const openCreate = () => { setCreateForm(CREATE_INIT); setCreateError(""); setShowCreate(true); };
  const closeCreate = () => setShowCreate(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setCreating(true); setCreateError("");
    try {
      const created = await api.create(societyId, createForm);
      setItems(p => [...p, created]);
      closeCreate();
    } catch (err) {
      setCreateError((err as ApiError).message || "Failed to create fund.");
    } finally { setCreating(false); }
  };

  const openTxn = (fund: Fund) => {
    setTxnTarget(fund);
    setTxnForm({ ...TXN_INIT, transactionDate: new Date().toISOString().slice(0, 10) });
    setTxnError("");
  };

  const handleTxn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !txnTarget) return;
    setTxnSaving(true); setTxnError("");
    try {
      const updated = await api.recordTransaction(societyId, txnTarget.id, {
        type: txnForm.type, amount: Number(txnForm.amount),
        transactionDate: txnForm.transactionDate, note: txnForm.note || undefined,
      });
      setItems(p => p.map(f => f.id === updated.id ? updated : f));
      setTxnTarget(null);
    } catch (err) {
      setTxnError((err as ApiError).message || "Failed to record transaction.");
    } finally { setTxnSaving(false); }
  };

  const handleDelete = (f: Fund) => { setConfirmFund(f); setDeleteError(""); };

  const confirmDelete = async () => {
    if (!societyId || !confirmFund) return;
    setDeletingId(confirmFund.id);
    try {
      await api.delete(societyId, confirmFund.id);
      setItems(p => p.filter(f => f.id !== confirmFund.id));
      setConfirmFund(null);
    } catch { setDeleteError("Failed to delete fund."); }
    finally { setDeletingId(null); }
  };

  const totalBalance = items.reduce((sum, f) => sum + f.balance, 0);

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Funds
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} funds · {fmtAmount(totalBalance)} total balance
          </p>
        </div>
        <button onClick={openCreate} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Fund
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "name", header: "Name", cellStyle: { fontWeight: 500 }, render: (f: Fund) => f.name },
            { key: "type", header: "Type", render: (f: Fund) => f.type.charAt(0) + f.type.slice(1).toLowerCase() },
            { key: "balance", header: "Balance", cellStyle: { fontWeight: 600 }, render: (f: Fund) => fmtAmount(f.balance) },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (f: Fund) => (
                <>
                  <button onClick={() => openTxn(f)} className="nx-btn nx-btn-outline nx-btn-sm" style={{ fontSize: 10, marginRight: 4 }}>
                    Contribute / Withdraw
                  </button>
                  <button onClick={() => handleDelete(f)} disabled={deletingId === f.id} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "#EF4444", opacity: deletingId === f.id ? 0.4 : 1, display: "inline-flex", alignItems: "center" }} title="Delete fund">
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={items}
          rowKey={f => f.id}
          loading={loading}
          emptyIcon={<Landmark size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No funds set up yet."
        />
      </div>

      <ConfirmDialog
        open={confirmFund !== null}
        variant="danger"
        title={`Delete "${confirmFund?.name}"?`}
        message="This fund and its transaction history will be permanently removed."
        confirmLabel="Delete Fund"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmFund(null); setDeleteError(""); }}
      />

      {showCreate && (
        <Modal
          title="Add Fund"
          submitLabel="Add Fund"
          error={createError}
          isLoading={creating}
          onClose={closeCreate}
          onSubmit={handleCreate}
        >
          <div>
            <label style={lbl}>Fund Name *</label>
            <input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} required placeholder="Building Sinking Fund" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Type *</label>
            <select value={createForm.type} onChange={e => setCreateForm(p => ({ ...p, type: e.target.value as Fund["type"] }))} style={inp}>
              <option value="SINKING">Sinking Fund</option>
              <option value="CORPUS">Corpus Fund</option>
              <option value="MAINTENANCE">Maintenance Fund</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </Modal>
      )}

      {txnTarget && (
        <Modal
          title={`${txnTarget.name} — Current Balance ${fmtAmount(txnTarget.balance)}`}
          submitLabel="Record Transaction"
          error={txnError}
          isLoading={txnSaving}
          onClose={() => setTxnTarget(null)}
          onSubmit={handleTxn}
        >
          <div>
            <label style={lbl}>Type *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setTxnForm(p => ({ ...p, type: "CONTRIBUTION" }))}
                className={txnForm.type === "CONTRIBUTION" ? "nx-btn nx-btn-primary nx-btn-sm" : "nx-btn nx-btn-outline nx-btn-sm"}
                style={{ flex: 1, gap: 4 }}
              >
                <ArrowUpCircle size={13} /> Contribution
              </button>
              <button
                type="button"
                onClick={() => setTxnForm(p => ({ ...p, type: "WITHDRAWAL" }))}
                className={txnForm.type === "WITHDRAWAL" ? "nx-btn nx-btn-primary nx-btn-sm" : "nx-btn nx-btn-outline nx-btn-sm"}
                style={{ flex: 1, gap: 4 }}
              >
                <ArrowDownCircle size={13} /> Withdrawal
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Amount (₹) *</label>
              <input type="number" value={txnForm.amount} onChange={e => setTxnForm(p => ({ ...p, amount: e.target.value }))} required min="0.01" step="0.01" style={inp} />
            </div>
            <div>
              <label style={lbl}>Date *</label>
              <input type="date" value={txnForm.transactionDate} onChange={e => setTxnForm(p => ({ ...p, transactionDate: e.target.value }))} required style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Note</label>
            <input value={txnForm.note} onChange={e => setTxnForm(p => ({ ...p, note: e.target.value }))} placeholder="Optional notes" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default FundsPage;
